import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const PRESETS = [6, 12, 24];

const addMonths = (base, months) => {
    const d = new Date(base);
    d.setMonth(d.getMonth() + months);
    return d.toISOString().slice(0, 10);
};

const baseDate = (end) => {
    const today = new Date().toLocaleDateString('sv-SE');
    return end && end > today ? end : today;
};

export const RenewLeaseDialog = ({ alert, el, onRenewed }) => {
    const [open, setOpen] = useState(false);
    const [endDate, setEndDate] = useState('');
    const [kmLimit, setKmLimit] = useState('');
    const [saving, setSaving] = useState(false);
    const base = baseDate(alert.contract_end_date);

    const save = async () => {
        setSaving(true);
        try {
            await api.post(`/leasing/vehicles/${alert.leasing_vehicle_id}/renew`, {
                contract_end_date: endDate || null,
                km_limit: kmLimit ? Number(kmLimit) : null,
            });
            toast.success(el ? 'Η σύμβαση ανανεώθηκε - ο μισθωτής ενημερώθηκε' : 'Contract renewed - lessee notified');
            setOpen(false);
            onRenewed();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };

    const fmt = (d) => new Date(d).toLocaleDateString(el ? 'el-GR' : 'en-GB');
    const id = alert.leasing_vehicle_id;

    return (
        <>
            <Button size="sm" onClick={() => { setEndDate(''); setKmLimit(''); setOpen(true); }} className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm" data-testid={`renew-lease-btn-${id}-${alert.type}`}>
                <RefreshCw className="w-4 h-4 mr-1" />{el ? 'Ανανέωση' : 'Renew'}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-white" data-testid="renew-lease-dialog">
                    <DialogHeader>
                        <DialogTitle>{el ? 'Ανανέωση σύμβασης' : 'Renew contract'} - {alert.license_plate}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-5">
                        <div className="space-y-2">
                            <Label>{el ? 'Παράταση λήξης' : 'Extend end date'}{alert.contract_end_date && <span className="text-xs text-slate-500 ml-2">({el ? 'τρέχουσα' : 'current'}: {fmt(alert.contract_end_date)})</span>}</Label>
                            <div className="grid grid-cols-3 gap-2">
                                {PRESETS.map((m) => {
                                    const value = addMonths(base, m);
                                    return (
                                        <Button key={m} type="button" variant="outline" onClick={() => setEndDate(value)} className={`rounded-sm ${endDate === value ? '!bg-[#1B7EB5] !text-white !border-[#1B7EB5]' : ''}`} data-testid={`renew-preset-${m}`}>
                                            +{m} {el ? 'μήνες' : 'months'}
                                        </Button>
                                    );
                                })}
                            </div>
                            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="renew-custom-date" />
                            <p className="text-xs text-slate-500">{el ? 'ή επιλέξτε άλλη ημερομηνία' : 'or pick another date'}</p>
                        </div>
                        <div className="space-y-1">
                            <Label>{el ? 'Νέο όριο χιλιομέτρων (προαιρετικό)' : 'New km limit (optional)'}{alert.km_limit && <span className="text-xs text-slate-500 ml-2">({el ? 'τρέχον' : 'current'}: {alert.km_limit.toLocaleString(el ? 'el-GR' : 'en-GB')} km)</span>}</Label>
                            <Input type="number" value={kmLimit} onChange={(e) => setKmLimit(e.target.value)} data-testid="renew-km-limit" />
                        </div>
                        <Button onClick={save} disabled={saving || (!endDate && !kmLimit)} className="w-full bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm" data-testid="renew-lease-save">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (el ? 'Επιβεβαίωση ανανέωσης' : 'Confirm renewal')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
